import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, ClipboardList, Calendar, Clock, ChevronDown, ChevronRight, Download, Trash2, User, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { format, parseISO } from 'date-fns';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Timesheets() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [timesheets, setTimesheets] = useState([]);
  const [foremen, setForemen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [foremanFilter, setForemanFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMonths, setExpandedMonths] = useState({});
  const [exporting, setExporting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTimesheet, setDeletingTimesheet] = useState(null);

  const isForeman = user?.role === 'FOREMAN';
  const isOffice = user?.role === 'OFFICE' || user?.role === 'OWNER';
  const isOwner = user?.role === 'OWNER';

  useEffect(() => {
    fetchTimesheets();
    if (isOffice || isOwner) {
      fetchForemen();
    }
  }, [statusFilter, token, isOffice, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchForemen = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/foremen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForemen(response.data);
    } catch (error) {
      console.error('Failed to fetch foremen:', error);
    }
  }, [token]);

  const fetchTimesheets = useCallback(async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/timesheets${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Sort by date descending
      const sorted = response.data.sort((a, b) => b.date.localeCompare(a.date));
      setTimesheets(sorted);
      
      // Auto-expand the most recent month
      if (sorted.length > 0) {
        const firstDate = sorted[0].date;
        const monthKey = firstDate.substring(0, 7); // YYYY-MM
        setExpandedMonths({ [monthKey]: true });
      }
    } catch (error) {
      console.error('Failed to fetch timesheets:', error);
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, token]);

  // Group timesheets by year and month (with foreman + search filter applied)
  const groupedTimesheets = useMemo(() => {
    let filtered = foremanFilter === 'all' 
      ? timesheets 
      : timesheets.filter(ts => ts.foremanId === foremanFilter);
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(ts =>
        ts.date?.toLowerCase().includes(q) ||
        ts.foremanName?.toLowerCase().includes(q) ||
        ts.shift?.toLowerCase().includes(q) ||
        ts.type?.toLowerCase().includes(q) ||
        ts.status?.toLowerCase().includes(q)
      );
    }

    const groups = {};
    filtered.forEach(ts => {
      const date = parseISO(ts.date);
      const year = date.getFullYear();
      const month = date.getMonth();
      const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
      const monthLabel = format(date, 'MMMM yyyy');
      
      if (!groups[year]) {
        groups[year] = {};
      }
      if (!groups[year][monthKey]) {
        groups[year][monthKey] = {
          label: monthLabel,
          timesheets: []
        };
      }
      groups[year][monthKey].timesheets.push(ts);
    });
    return groups;
  }, [timesheets, foremanFilter, searchQuery]);

  // Get foreman name for display
  const getForemanName = (foremanId) => {
    const foreman = foremen.find(f => f.id === foremanId);
    return foreman?.name || 'Unknown';
  };

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  const handleExportTimesheets = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (foremanFilter !== 'all') params.append('foremanId', foremanFilter);
      
      const response = await axios.get(`${API}/export/timesheets?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `labor_entries_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      console.error('Failed to export:', error);
      toast.error('Failed to export timesheets');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteTimesheet = async () => {
    if (!deletingTimesheet) return;
    
    try {
      await axios.delete(`${API}/timesheets/${deletingTimesheet.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Timesheet deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingTimesheet(null);
      fetchTimesheets();
    } catch (error) {
      console.error('Failed to delete timesheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete timesheet');
    }
  };

  const handleDeleteClick = (e, timesheet) => {
    e.stopPropagation(); // Prevent navigation to timesheet detail
    setDeletingTimesheet(timesheet);
    setDeleteDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const handleCreateTimesheet = () => {
    navigate('/timesheets/new');
  };

  const handleViewTimesheet = (timesheetId) => {
    navigate(`/timesheets/${timesheetId}`);
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading timesheets...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="timesheets-page">
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            {isForeman ? 'My Timesheets' : 'All Timesheets'}
          </h1>
          <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-700">
            {isForeman ? 'Create and submit crew timesheets' : 'Review and approve submitted timesheets'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {isOffice && (
            <Button
              onClick={handleExportTimesheets}
              disabled={exporting}
              variant="outline"
              data-testid="export-timesheets-button"
              className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-[#1A3668] hover:text-white rounded-sm py-3 md:py-2"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export All'}
            </Button>
          )}
          {(isForeman || isOffice || isOwner) && (
            <Button
              onClick={handleCreateTimesheet}
              data-testid="create-timesheet-button"
              className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm shadow-sm transition-all active:scale-95 py-4 md:py-2 text-base"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Timesheet
            </Button>
          )}
        </div>
      </div>

      {/* Filters - Mobile Scrollable */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <Button
          onClick={() => setStatusFilter('')}
          variant={statusFilter === '' ? 'default' : 'outline'}
          className={`${statusFilter === '' ? 'bg-[#1A3668] text-white' : ''} rounded-sm whitespace-nowrap flex-shrink-0`}
          size="sm"
        >
          All
        </Button>
        <Button
          onClick={() => setStatusFilter('DRAFT')}
          variant={statusFilter === 'DRAFT' ? 'default' : 'outline'}
          className={`${statusFilter === 'DRAFT' ? 'bg-[#1A3668] text-white' : ''} rounded-sm whitespace-nowrap flex-shrink-0`}
          size="sm"
        >
          Draft
        </Button>
        <Button
          onClick={() => setStatusFilter('SUBMITTED')}
          variant={statusFilter === 'SUBMITTED' ? 'default' : 'outline'}
          className={`${statusFilter === 'SUBMITTED' ? 'bg-[#1A3668] text-white' : ''} rounded-sm whitespace-nowrap flex-shrink-0`}
          size="sm"
        >
          Submitted
        </Button>
        <Button
          onClick={() => setStatusFilter('APPROVED')}
          variant={statusFilter === 'APPROVED' ? 'default' : 'outline'}
          className={`${statusFilter === 'APPROVED' ? 'bg-[#1A3668] text-white' : ''} rounded-sm whitespace-nowrap flex-shrink-0`}
          size="sm"
        >
          Approved
        </Button>
      </div>

      {/* Foreman Filter - Show for Office and Owner */}
      {(isOffice || isOwner) && foremen.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row gap-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Filter by Submitted By</Label>
            <Select value={foremanFilter} onValueChange={setForemanFilter}>
              <SelectTrigger className="w-full sm:w-[250px] rounded-sm border-2" data-testid="foreman-filter">
                <User className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="All Foremen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Foremen</SelectItem>
                {foremen.map((foreman) => (
                  <SelectItem key={foreman.id} value={foreman.id}>
                    {foreman.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Search</Label>
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
              <Input data-testid='timesheet-search' value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder='Search timesheets...' className='pl-9 rounded-sm border-2 w-full sm:w-[250px]' />
            </div>
          </div>
        </div>
      )}

      {timesheets.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <ClipboardList className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-4">
              {statusFilter ? `No ${statusFilter.toLowerCase()} timesheets` : 'No timesheets yet'}
            </p>
            {isForeman && (
              <Button
                onClick={handleCreateTimesheet}
                variant="outline"
                className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm"
              >
                Create your first timesheet
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.keys(groupedTimesheets).sort((a, b) => b - a).map(year => (
            <div key={year} className="space-y-2">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 px-1">{year}</h2>
              {Object.keys(groupedTimesheets[year]).sort((a, b) => b.localeCompare(a)).map(monthKey => {
                const { label, timesheets: monthTimesheets } = groupedTimesheets[year][monthKey];
                const isExpanded = expandedMonths[monthKey];
                const filteredMonthTimesheets = statusFilter 
                  ? monthTimesheets.filter(t => t.status === statusFilter)
                  : monthTimesheets;
                
                if (filteredMonthTimesheets.length === 0) return null;
                
                return (
                  <Collapsible 
                    key={monthKey} 
                    open={isExpanded} 
                    onOpenChange={() => toggleMonth(monthKey)}
                  >
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center justify-between px-4 py-3 bg-slate-100 rounded-sm cursor-pointer hover:bg-slate-200 transition-colors">
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          )}
                          <span className="font-bold text-slate-800">{label}</span>
                          <Badge variant="secondary" className="rounded-sm bg-slate-200 text-slate-600">
                            {filteredMonthTimesheets.length} {filteredMonthTimesheets.length === 1 ? 'timesheet' : 'timesheets'}
                          </Badge>
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 pb-2">
                        {filteredMonthTimesheets.map((timesheet) => (
                          <Card
                            key={timesheet.id}
                            data-testid={`timesheet-card-${timesheet.id}`}
                            onClick={() => handleViewTimesheet(timesheet.id)}
                            className="border-slate-200 shadow-sm hover:shadow-md hover:border-[#1A3668] transition-all cursor-pointer rounded-sm"
                          >
                            <CardContent className="p-6">
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Calendar className="w-4 h-4 text-[#1A3668]" />
                                    <h3 className="text-xl font-bold text-slate-900">
                                      {timesheet.type === 'WEEKLY' 
                                        ? `Week of ${timesheet.date}` 
                                        : timesheet.date}
                                    </h3>
                                    {timesheet.type === 'WEEKLY' && (
                                      <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-sm">WEEKLY</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <Clock className="w-3 h-3" />
                                    <span>{timesheet.shift}</span>
                                  </div>
                                  {/* Show foreman name for Office/Owner */}
                                  {(isOffice || isOwner) && timesheet.foremanId && (
                                    <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                                      <User className="w-3 h-3" />
                                      <span>{getForemanName(timesheet.foremanId)}</span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                  <Badge className={`rounded-sm ${getStatusColor(timesheet.status)}`}>
                                    {timesheet.status}
                                  </Badge>
                                  {/* Show delete button for draft timesheets (Office or Foreman owner) */}
                                  {timesheet.status === 'DRAFT' && (isOffice || (isForeman && timesheet.foremanId === user?.id)) && (
                                    <Button
                                      onClick={(e) => handleDeleteClick(e, timesheet)}
                                      variant="outline"
                                      size="sm"
                                      data-testid={`delete-timesheet-${timesheet.id}`}
                                      className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>

                              {timesheet.submittedAt && (
                                <div className="text-xs text-slate-500 mt-2">
                                  Submitted: {format(new Date(timesheet.submittedAt), 'MMM d, yyyy h:mm a')}
                                </div>
                              )}
                              {timesheet.approvedAt && (
                                <div className="text-xs text-green-600 mt-1">
                                  Approved: {format(new Date(timesheet.approvedAt), 'MMM d, yyyy h:mm a')}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Delete Timesheet Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Draft Timesheet</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the timesheet for <strong>{deletingTimesheet?.date}</strong> ({deletingTimesheet?.shift} shift)?
              <br /><br />
              This will also delete all labor entries associated with this timesheet. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" onClick={() => setDeletingTimesheet(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTimesheet}
              data-testid="confirm-delete-timesheet"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Delete Timesheet
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
